import {useCallback, useEffect, useRef, useState} from 'react';

import {useConfig} from 'src/modules/config';
import {
    getUserAiTokenLimits,
    saveUserAiTokenOverrides,
    saveUserAiTokenTemporaryOverrides,
} from 'src/openapi/admin/apis/UserAiTokenLimitsApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import {AI_TOKEN_PERIODS, areAiTokenLimitsEqual, isAiTokenAmountValid, toAiTokenExpiry, toAiTokenUntil} from '../utils/ai-token-limits';

import type {SaveOptions} from 'src/components/save-with-reason/save-with-reason-button';
import type {AiTokenLimit} from 'src/openapi/admin/models/AiTokenLimit';
import type {AiTokenLimitPeriod} from 'src/openapi/admin/models/AiTokenLimitPeriod';
import type {AiTokenLimits} from 'src/openapi/admin/models/AiTokenLimits';
import type {AiTokenTemporaryLimit} from 'src/openapi/admin/models/AiTokenTemporaryLimit';
import type {Error as ApiError} from 'src/openapi/admin/models/Error';

export type AiTokenTemporarySettings = {until: string, limits: AiTokenTemporaryLimit[]};
const EMPTY: AiTokenLimits = {defaults: [], limits: [], overrides: [], temporaryOverrides: []};
const EMPTY_TEMPORARY: AiTokenTemporarySettings = {until: '', limits: []};

export const useUserAiTokenLimits = ({userId}: {userId: number}) => {
    const dispatch = useAppDispatch();
    const {timeZone} = useConfig();
    const [data, setData] = useState<AiTokenLimits>(EMPTY);
    const [overrides, setOverrides] = useState<AiTokenLimit[]>([]);
    const [temporary, setTemporary] = useState<AiTokenTemporarySettings>(EMPTY_TEMPORARY);
    const [initialOverrides, setInitialOverrides] = useState<AiTokenLimit[]>([]);
    const [initialTemporary, setInitialTemporary] = useState<AiTokenTemporarySettings>(EMPTY_TEMPORARY);
    const [isLoading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<ApiError | null>(null);
    const request = useRef<AbortController | null>(null);
    const saving = useRef(false);
    const baseline = useRef({userId: 0, overrides: [] as AiTokenLimit[], temporary: EMPTY_TEMPORARY});

    useEffect(() => {
        const controller = new AbortController();
        request.current = controller;
        setLoading(true);
        setLoadingError(null);
        setData(EMPTY);
        setOverrides([]);
        setTemporary(EMPTY_TEMPORARY);
        setInitialOverrides([]);
        setInitialTemporary(EMPTY_TEMPORARY);
        baseline.current = {userId: 0, overrides: [], temporary: EMPTY_TEMPORARY};
        const load = async () => {
            try {
                const response = await dispatch(getUserAiTokenLimits({userId}, {signal: controller.signal}));
                if (controller.signal.aborted) {
                    return;
                }
                if (response.payload.error) {
                    setLoadingError(response.payload.data);
                } else {
                    const loaded = response.payload.data;
                    if (!AI_TOKEN_PERIODS.every(period => loaded.defaults.some(limit => limit.period === period))) {
                        setLoadingError({message: 'PitchBook AI default limits are unavailable'});
                        return;
                    }
                    const temp = {
                        until: loaded.temporaryOverrides.length
                            ? toAiTokenUntil(loaded.temporaryOverrides[0].expiresOn, timeZone) : '',
                        limits: loaded.temporaryOverrides,
                    };
                    setData(loaded);
                    setOverrides(loaded.overrides);
                    setTemporary(temp);
                    setInitialOverrides(loaded.overrides);
                    setInitialTemporary(temp);
                    baseline.current = {userId, overrides: loaded.overrides, temporary: temp};
                }
            } catch {
                if (!controller.signal.aborted) {
                    setLoadingError({message: 'Unable to load PitchBook AI token limits'});
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => controller.abort();
    }, [dispatch, userId, timeZone]);

    const isChanged = !areAiTokenLimitsEqual(overrides, initialOverrides) ||
        !areAiTokenLimitsEqual(temporary.limits, initialTemporary.limits) ||
        temporary.until !== initialTemporary.until;

    const validateChanges = useCallback((): string | undefined => {
        if (temporary.until && !temporary.limits.length) {
            return 'At least one temporary AI token limit must be specified';
        }
        if (![...overrides, ...temporary.limits].every(limit => isAiTokenAmountValid(limit.amount))) {
            return 'AI token limits must be non-negative safe integers';
        }
        if (temporary.limits.some(limit => !limit.expiresOn || Date.parse(limit.expiresOn) <= Date.now() ||
            !Number.isFinite(Date.parse(limit.expiresOn)))) {
            return 'Temporary AI token limits must expire in the future';
        }
        return undefined;
    }, [overrides, temporary]);

    const saveChanges = useCallback(async (options?: SaveOptions): Promise<string | undefined> => {
        const validationError = validateChanges();
        if (validationError) {
            return validationError;
        }
        if (isLoading || loadingError || baseline.current.userId !== userId) {
            return 'PitchBook AI token limits have not loaded';
        }
        if (saving.current) {
            return 'PitchBook AI token limits are being saved';
        }
        saving.current = true;
        const errors: string[] = [];
        const signal = request.current!.signal;
        try {
            if (!areAiTokenLimitsEqual(overrides, baseline.current.overrides)) {
                const result = await dispatch(saveUserAiTokenOverrides({
                    userId,
                    userAiTokenOverridesSaveRequest: {limits: overrides, reason: options?.reason},
                }, {signal}));
                if (signal.aborted) {
                    return 'Saving PitchBook AI token limits was cancelled';
                }
                if (result.payload.error) {
                    errors.push(getError({error: result.payload.data}) || 'Unable to save AI token overrides');
                } else {
                    baseline.current.overrides = result.payload.data;
                    setInitialOverrides(result.payload.data);
                    setOverrides(result.payload.data);
                }
            }
            if (!areAiTokenLimitsEqual(temporary.limits, baseline.current.temporary.limits)) {
                const result = await dispatch(saveUserAiTokenTemporaryOverrides({
                    userId,
                    userAiTokenTemporaryOverridesSaveRequest: {limits: temporary.limits, reason: options?.reason},
                }, {signal}));
                if (signal.aborted) {
                    return 'Saving PitchBook AI token limits was cancelled';
                }
                if (result.payload.error) {
                    errors.push(getError({error: result.payload.data}) || 'Unable to save temporary AI token limits');
                } else {
                    const saved = {...temporary, limits: result.payload.data};
                    baseline.current.temporary = saved;
                    setInitialTemporary(saved);
                    setTemporary(saved);
                }
            }
            if (!errors.length) {
                // Mode/date changes with no payload change must not issue a PUT.
                baseline.current.temporary = temporary;
                setInitialTemporary(temporary);
            }
            return errors.length ? errors.join(' ') : undefined;
        } catch {
            return 'Unable to save PitchBook AI token limits';
        } finally {
            saving.current = false;
        }
    }, [dispatch, userId, overrides, temporary, validateChanges, isLoading, loadingError]);

    const discardChanges = useCallback(() => {
        setOverrides(initialOverrides);
        setTemporary(initialTemporary);
    }, [initialOverrides, initialTemporary]);

    const setUntil = useCallback((until: string) => {
        setTemporary(previous => ({
            until,
            limits: until ? previous.limits.map(limit => {
                const original = baseline.current.temporary.limits.find(item => item.period === limit.period);
                const expiresOn = until === baseline.current.temporary.until && original
                    ? original.expiresOn : toAiTokenExpiry(until, timeZone);
                return {...limit, expiresOn};
            }) : [],
        }));
    }, [timeZone]);

    const clearTemporaryLimit = useCallback((period: AiTokenLimitPeriod) => {
        setTemporary(previous => {
            const limits = previous.limits.filter(limit => limit.period !== period);
            return {limits, until: limits.length ? previous.until : ''};
        });
    }, []);

    const resetLimits = useCallback(() => {
        setOverrides([]);
        setTemporary(EMPTY_TEMPORARY);
    }, []);

    return {
        data, overrides, setOverrides, temporary, setTemporary, setUntil, clearTemporaryLimit,
        isLoading, loadingError, isChanged, validateChanges, saveChanges, discardChanges, resetLimits,
    };
};
